/**
 * Utilidades de formateo y normalización de texto.
 * Basado en cleanString y otros de Utilidades.java del legacy GWT.
 */

// ============================================
// CONSTANTES PARA LIMPIEZA DE ACENTOS
// ============================================

/** Caracteres ASCII equivalentes (sin acentos) */
const PLAIN_ASCII = 
  'AaEeIiOoUu' +      // grave
  'AaEeIiOoUuYy' +    // acute
  'AaEeIiOoUuYy' +    // circumflex
  'AaOoNn' +          // tilde
  'AaEeIiOoUuYy' +    // umlaut
  'Aa' +              // ring
  'Cc' +              // cedilla
  'OoUu';             // double acute

/** Caracteres Unicode con acentos */
const UNICODE = 
  '\u00C0\u00E0\u00C8\u00E8\u00CC\u00EC\u00D2\u00F2\u00D9\u00F9' +       // grave
  '\u00C1\u00E1\u00C9\u00E9\u00CD\u00ED\u00D3\u00F3\u00DA\u00FA\u00DD\u00FD' + // acute
  '\u00C2\u00E2\u00CA\u00EA\u00CE\u00EE\u00D4\u00F4\u00DB\u00FB\u0176\u0177' + // circumflex
  '\u00C3\u00E3\u00D5\u00F5\u00D1\u00F1' +                                    // tilde
  '\u00C4\u00E4\u00CB\u00EB\u00CF\u00EF\u00D6\u00F6\u00DC\u00FC\u0178\u00FF' + // umlaut
  '\u00C5\u00E5' +                                                            // ring
  '\u00C7\u00E7' +                                                            // cedilla
  '\u0150\u0151\u0170\u0171';                                                 // double acute

// ============================================
// FUNCIONES DE LIMPIEZA
// ============================================

/**
 * Elimina acentos de un texto, reemplazándolos por sus equivalentes ASCII.
 * Equivale a cleanString de Utilidades.java (parcialmente).
 */
export function removeAccents(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  
  let result = '';
  for (let i = 0; i < value.length; i++) {
    const char = value.charAt(i);
    const pos = UNICODE.indexOf(char);
    if (pos > -1) {
      result += PLAIN_ASCII.charAt(pos);
    } else {
      result += char;
    }
  }
  return result;
}

/**
 * Limpia string: elimina acentos y caracteres especiales, dejando solo letras y espacios.
 * Equivale a cleanString completo de Utilidades.java.
 */
export function cleanString(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  return removeAccents(value).replace(/[^a-zA-Z\s]/g, '');
}

/**
 * Limpia string pero conserva números (alfanumérico clean).
 */
export function cleanStringAlphanumeric(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  return removeAccents(value).replace(/[^a-zA-Z0-9\s]/g, '');
}

// ============================================
// FUNCIONES DE NORMALIZACIÓN
// ============================================

/**
 * Convierte a mayúsculas y elimina espacios al inicio/final.
 * Comportamiento estándar de TextBoxChangeHandler del legacy.
 */
export function normalizeUpperTrim(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  return value.toUpperCase().trim();
}

/**
 * Convierte a minúsculas y elimina espacios al inicio/final.
 */
export function normalizeLowerTrim(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  return value.toLowerCase().trim();
}

/**
 * Capitaliza la primera letra de cada palabra.
 */
export function capitalizeWords(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  return value
    .toLowerCase()
    .split(' ')
    .filter(word => word.length > 0)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Capitaliza solo la primera letra del texto.
 */
export function capitalizeFirst(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

/**
 * Elimina espacios múltiples consecutivos.
 */
export function normalizeSpaces(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  return value.replace(/\s+/g, ' ').trim();
}

/**
 * Limpia completamente: mayúsculas, sin acentos, sin caracteres especiales.
 * Para guardar en base de datos como texto normalizado.
 */
export function normalizeForStorage(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  return normalizeUpperTrim(cleanString(normalizeSpaces(value)));
}

// ============================================
// FORMATEO DE NOMBRES
// ============================================

/**
 * Normaliza un nombre: capitaliza cada palabra
 */
export function formatName(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  return capitalizeWords(normalizeSpaces(value));
}

/**
 * Combina nombre y apellidos en nombre completo.
 */
export function formatFullName(
  nombre1: string | null | undefined,
  nombre2: string | null | undefined,
  apellido1: string | null | undefined,
  apellido2: string | null | undefined
): string {
  const parts = [nombre1, nombre2, apellido1, apellido2]
    .filter(part => part && part.trim() !== '')
    .map(part => part!.trim());
  return parts.join(' ');
}

// ============================================
// FORMATEO DE NÚMEROS
// ============================================

/**
 * Formatea un número con separadores de miles y decimales.
 * @param value - Número a formatear
 * @param decimals - Cantidad de decimales (default: 2)
 * @param thousandSep - Separador de miles (default: '.')
 * @param decimalSep - Separador decimal (default: ',')
 */
export function formatNumber(
  value: number | null | undefined,
  decimals: number = 2,
  thousandSep: string = '.',
  decimalSep: string = ','
): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '';
  }
  
  const fixed = value.toFixed(decimals);
  const [intPart, decPart] = fixed.split('.');
  
  // Agregar separador de miles
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSep);
  
  return decPart !== undefined ? `${formattedInt}${decimalSep}${decPart}` : formattedInt;
}

/**
 * Formatea un valor como moneda colombiana (pesos).
 */
export function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '';
  }
  return `$ ${formatNumber(value, 0)}`;
}

/**
 * Formatea un valor como porcentaje.
 */
export function formatPercent(value: number | null | undefined, decimals: number = 2): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '';
  }
  return `${formatNumber(value, decimals)} %`;
}

/**
 * Parsea un número formateado de vuelta a number.
 */
export function parseFormattedNumber(
  value: string | null | undefined,
  thousandSep: string = '.',
  decimalSep: string = ','
): number | null {
  if (!value) {
    return null;
  }
  
  // Remover separador de miles y cambiar separador decimal a punto
  const cleaned = value
    .replace(/[^0-9.,\-]/g, '')
    .replace(new RegExp(`\\${thousandSep}`, 'g'), '')
    .replace(decimalSep, '.');
  
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? null : parsed;
}

// ============================================
// FORMATEO DE IDENTIFICACIÓN
// ============================================

/**
 * Formatea número de identificación con puntos de miles.
 */
export function formatIdentificacion(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  // Limpiar y dejar solo números
  const clean = value.replace(/[^\d]/g, '');
  // Agregar puntos de miles
  return clean.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/**
 * Limpia formato de identificación (quita puntos).
 */
export function unformatIdentificacion(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  return value.replace(/\./g, '');
}

// ============================================
// FORMATEO DE TELÉFONO
// ============================================

/**
 * Formatea número de teléfono colombiano.
 * Ejemplo: 3001234567 → 300 123 4567
 */
export function formatPhone(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  const clean = value.replace(/[^\d]/g, '');
  
  if (clean.length === 10) {
    // Celular: 300 123 4567
    return `${clean.slice(0, 3)} ${clean.slice(3, 6)} ${clean.slice(6)}`;
  } else if (clean.length === 7) {
    // Fijo: 123 4567
    return `${clean.slice(0, 3)} ${clean.slice(3)}`;
  }
  
  return value;
}

// ============================================
// TRUNCADO DE TEXTO
// ============================================

/**
 * Trunca un texto a una longitud máxima, agregando "..." si se trunca.
 */
export function truncate(value: string | null | undefined, maxLength: number, suffix: string = '...'): string {
  if (!value) {
    return '';
  }
  if (value.length <= maxLength) {
    return value;
  }
  return value.substring(0, maxLength - suffix.length) + suffix;
}

/**
 * Trunca un texto respetando límites de palabra.
 */
export function truncateWords(value: string | null | undefined, maxLength: number, suffix: string = '...'): string {
  if (!value) {
    return '';
  }
  if (value.length <= maxLength) {
    return value;
  }
  
  const truncated = value.substring(0, maxLength - suffix.length);
  const lastSpace = truncated.lastIndexOf(' ');
  
  if (lastSpace > 0) {
    return truncated.substring(0, lastSpace) + suffix;
  }
  
  return truncated + suffix;
}
